import SideBarContainer from 'Containers/SideBarContainer';
import { useHistory, useParams } from 'react-router-dom';
import Breadcrumb from 'Components/Breadcrumb/Breadcrumb';
import { Padding } from 'Components/Padding';
import useCourseEmployeesPage from './hooks/useCourseEmployeesPage';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import GenericTable from '../employees/components/GenericTable/GenericTable';
import TotalCourseDetails from './components/TotalCourseDetails/TotalCourseDetails';
import type { CourseEmployeeReportRowI } from 'types';
import ProgressIndicator from 'Components/ProgressIndicator';
import CourseStatusChip from 'Components/CourseStatusChip';
import CertificateDownload from 'Components/CertificateDownload/CertificateDownload';
import CourseEmployeesFilters from './components/CourseEmployeesFilters/CourseEmployeesFilters';
import { FaPeopleGroup } from 'react-icons/fa6';
import { MdSchool } from 'react-icons/md';

interface Params {
  courseId: string;
}

export default function Page() {
  const { courseId } = useParams<Params>();
  const history = useHistory();
  const { t2, course, totals, courseEmployees, onDownloadCertificate, onExportExcel, onSearch } =
    useCourseEmployeesPage(courseId);

  const buttons = [
    { icon: <FaPeopleGroup size={24} />, tooltip: t2('employees_report_tooltip'), route: '/reporte/empleados' },
    { icon: <MdSchool size={24} />, tooltip: t2('courses_report_tooltip'), route: '/reporte/cursos' },
  ];

  return (
    <SideBarContainer>
      <Padding padding="1rem 1rem 0 1rem">
        <Breadcrumb
          items={[
            { label: t2('courses_report'), path: '/reporte/cursos' },
            { label: t2('course_detail'), path: `/reporte/cursos/${courseId}` },
          ]}
        />
      </Padding>
      <ReportsLayout
        title={<ReportsTitle title={t2('title')} accent={`"${course?.name}"`} />}
        buttons={buttons}
        onExport={onExportExcel}
      />
      <Padding padding="1rem">
        <TotalCourseDetails totals={totals} />
        <CourseEmployeesFilters onSearch={onSearch} />
        <Padding padding="0.5rem 0" />
        <GenericTable<CourseEmployeeReportRowI>
          columns={[
            { id: 'payrollNum', label: t2('payroll_number'), align: 'center' },
            { id: 'fullName', label: t2('full_name') },
            { id: 'departmentName', label: t2('department_name') },
            { id: 'jobTitleName', label: t2('job_title_name') },
            { id: 'jobTitleTypeName', label: t2('job_title_type_name') },
            { id: 'gender', label: t2('gender') },
            { id: 'age', label: t2('age') },
            { id: 'entryDate', label: t2('entry_date') },
            { id: 'completedEvaluations', label: t2('completed_evaluations'), align: 'center' },
            { id: 'userCourseStatusName', label: t2('user_course_status_name'), align: 'center' },
            { id: 'downloadCertificate', label: t2('download_certificate'), align: 'center' },
          ]}
          rows={courseEmployees.map(employee => ({
            id: employee.id,
            payrollNum: employee.payrollNum,
            fullName: employee.fullName,
            departmentName: employee.departmentName,
            jobTitleName: employee.jobTitleName,
            jobTitleTypeName: employee.jobTitleTypeName,
            gender: employee.gender,
            age: employee.age,
            entryDate: employee.entryDate,
            completedEvaluations: (
              <ProgressIndicator
                completed={employee.completedEvaluations}
                total={employee.totalEvaluations}
                emptyMessage={t2('no_evaluations')}
              />
            ),
            userCourseStatusName: (
              <CourseStatusChip status={employee.userCourseStatusName} statusId={employee.userCourseStatusId} />
            ),
            userCourseStatusId: employee.userCourseStatusId,
            terminationDate: employee.terminationDate,
            certificationId: employee.certificationId,
            downloadCertificate: (
              <CertificateDownload
                certificationId={employee.certificationId}
                onDownload={() => onDownloadCertificate(courseId, employee.id, employee.certificationId)}
              />
            ),
          }))}
          onRowClick={row => {
            history.push(`/reporte/empleados/${row.id}`);
          }}
        />
      </Padding>
    </SideBarContainer>
  );
}
